/**
 * AuthForm.jsx — Sign-in screen (MUI)
 * Google Sign-In is the ONLY authentication path (2026-07-28). Accounts are
 * invite-only: POST /auth/google looks the email up in profiles and refuses
 * anything it doesn't already know. There is no password login to fall back to.
 */

import { useEffect, useRef, useCallback } from 'react';
import { Box, Paper, Typography, Alert } from '@mui/material';
import { useAuth } from '../../hooks/useAuth';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

export default function AuthForm() {
  const { loginWithGoogle, authError } = useAuth();
  const googleButtonRef = useRef(null);

  const handleGoogleCallback = useCallback((response) => {
    if (response.credential) {
      loginWithGoogle(response.credential);
    }
  }, [loginWithGoogle]);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    const initGoogle = () => {
      if (!window.google?.accounts?.id) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleCallback,
        auto_select: false,
        cancel_on_tap_outside: true,
      });
      if (googleButtonRef.current) {
        const w = Math.min(400, Math.max(200, googleButtonRef.current?.offsetWidth || 352));
        window.google.accounts.id.renderButton(googleButtonRef.current, {
          theme: 'outline',
          size: 'large',
          width: w,
          text: 'signin_with',
        });
      }
    };
    if (window.google?.accounts?.id) { initGoogle(); return; }
    const timer = setInterval(() => {
      if (window.google?.accounts?.id) { clearInterval(timer); initGoogle(); }
    }, 200);
    return () => clearInterval(timer);
  }, [handleGoogleCallback]);

  return (
    <Box sx={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default', px: 2 }}>
      <Box sx={{ width: '100%', maxWidth: 400 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h3" sx={{ fontFamily: '"Bebas Neue", sans-serif', letterSpacing: 3, color: 'text.primary' }}>
            ROTO FLIGHT OS
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Video Production Operations
          </Typography>
        </Box>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            Sign In
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Use your Roto Flight Google account.
          </Typography>

          {authError && <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>{authError}</Alert>}

          {GOOGLE_CLIENT_ID ? (
            <Box ref={googleButtonRef} sx={{ display: 'flex', justifyContent: 'center' }} />
          ) : (
            <Alert severity="error" variant="outlined">
              Google Sign-In is not configured (VITE_GOOGLE_CLIENT_ID is missing), so there is no way
              to sign in. Contact your administrator.
            </Alert>
          )}
        </Paper>

        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', mt: 3 }}>
          Roto Flight LLC &middot; Santa Barbara, CA
        </Typography>
      </Box>
    </Box>
  );
}
